import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hero } from '@/components/home/Hero';
import { BearsCollection } from '@/components/home/BearsCollection';
import { BearsShowcase } from '@/components/home/BearsShowcase';
import { CategoryTiles } from '@/components/home/CategoryTiles';
import { HowItWorks } from '@/components/home/HowItWorks';
import { Reviews } from '@/components/home/Reviews';
import { PrivacyFaq } from '@/components/home/PrivacyFaq';
import { StructuredData } from '@/components/home/StructuredData';
import { EmailCapture } from '@/components/EmailCapture';
import { fetchCategories, fetchProducts, pickBears } from '@/lib/catalog';
import { useInitialData } from '@/lib/initialData';

export const Home = ({ onAddToCart, popupEnabled = true }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const initial = useInitialData();
  const [products, setProducts] = useState(() => (initial.products ? pickBears(initial.products) : []));
  const [categories, setCategories] = useState(() => initial.categories || []);
  const [loading, setLoading] = useState(!initial.products);

  // Always refresh: prerendered data may be older than the last deploy
  useEffect(() => {
    fetchProducts()
      .then((all) => setProducts(pickBears(all)))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  // Anchor links (/#bears, /#how, /#faq) — scroll once the target exists
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, loading, categories.length]);

  useEffect(() => {
    document.title = t('meta.homeTitle');
  }, [t]);

  const faqs = t('faq.items', { returnObjects: true });

  return (
    <main data-testid="home-page">
      <Hero />
      <BearsCollection products={products} loading={loading} onAddToCart={onAddToCart} />
      <BearsShowcase />
      <CategoryTiles categories={categories} />
      <HowItWorks />
      <Reviews />
      <PrivacyFaq faqs={faqs} />
      {popupEnabled && <EmailCapture />}
      {products.length > 0 && <StructuredData products={products} faqs={faqs} />}
    </main>
  );
};
